import { MenuIcon } from "@xola/icons";
import clsx from "clsx";
import React from "react";
import { DragDropContext, Draggable, Droppable, DroppableProvided, DropResult } from "react-beautiful-dnd";
import { Product, ProductObject, SelectionState } from "types";
import { useResource } from "../../hooks/useResource";
import { NoTranslate } from "./NoTranslate";
import { ProductImage } from "./ProductImage";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { reorder } from "@/utils/reorder";

interface SelectedProductListProps {
    selectedProducts: SelectionState;
    selectedAllText?: string;
    selectedNoneText?: string;
    isError?: boolean;
    showDragAndDropProductList?: boolean;
    className?: string;
    onChange?: (value: SelectionState) => void;
}

export const SelectedProductList = ({
    selectedProducts,
    selectedAllText = "All products are selected",
    selectedNoneText = "No products selected",
    isError = false,
    showDragAndDropProductList = false,
    className = "",
    onChange,
}: SelectedProductListProps) => {
    const { data: activeProducts, isLoading } = useAutoFetchCollection<Product>(
        "products",
        { "object[in]": ProductObject.EXPERIENCE },
        {
            keepPreviousData: true,
        },
    );

    return (
        <div
            className={clsx(
                "divide-y divide-gray-lighter rounded-lg border",
                !showDragAndDropProductList && "max-h-40 overflow-y-auto",
                isError ? "border-danger" : "border-gray-lighter",
                className,
            )}
        >
            {selectedProducts.selected.length > 0 ? (
                showDragAndDropProductList ? (
                    <ProductItemDragAndDrop selectedProducts={selectedProducts.selected} onChange={onChange} />
                ) : (
                    selectedProducts.selected.map((id) => <ProductItem key={id} id={id} />)
                )
            ) : (
                <div className="py-3 px-4 text-gray-dark" data-testid="select-product-list">
                    {isLoading
                        ? "Loading..."
                        : selectedProducts.all && activeProducts.length > 0
                        ? selectedAllText
                        : selectedNoneText}
                </div>
            )}
        </div>
    );
};

const ProductItem = ({ id }: { id: ID }) => {
    const { data: product } = useResource<Product>(["products", id]);

    return (
        <span className="flex items-center py-2 px-3" data-testid="selected-product-list-item">
            <ProductImage product={{ id, name: product?.name, object: product?.object }} size="tiny" />
            <NoTranslate className="ml-2 font-bold text-black">{product?.name.trim()}</NoTranslate>
        </span>
    );
};

const ProductItemDragAndDrop = ({
    selectedProducts,
    onChange,
}: {
    selectedProducts: ID[];
    onChange?: (value: SelectionState) => void;
}) => {
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const reordered = reorder(selectedProducts, result.source.index, result.destination.index);
        onChange?.({ all: false, selected: reordered });
    };

    return (
        <DragDropContext onDragEnd={async (result: DropResult) => handleDragEnd(result)}>
            <Droppable droppableId="productDragAndDropList">
                {(provided: DroppableProvided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                        {selectedProducts.map((id, index) => (
                            <Draggable key={id} draggableId={id} index={index}>
                                {({ draggableProps, dragHandleProps, innerRef }, snapshot) => (
                                    <div
                                        ref={innerRef}
                                        {...draggableProps}
                                        {...dragHandleProps}
                                        className={clsx("flex items-center", {
                                            shadow: snapshot.isDragging,
                                        })}
                                    >
                                        <MenuIcon size="small" className="ml-2" />
                                        <ProductItem key={id} id={id} />
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};
